import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { LuCalendarCheck, LuChartNoAxesCombined, LuFileSpreadsheet, LuFolderKanban, LuUsers, LuWallet } from 'react-icons/lu';
import DataCardList from '../components/data/DataCardList';
import DataTable from '../components/data/DataTable';
import DataToolbar from '../components/data/DataToolbar';
import FilterSheet from '../components/data/FilterSheet';
import PageHero from '../components/data/PageHero';
import StatTiles from '../components/data/StatTiles';
import TablePagination from '../components/data/TablePagination';
import { SortControl } from '../components/data/ToolbarSelect';
import Button from '../components/ui/Button';
import useElementWidth from '../hooks/useElementWidth';
import useMediaQuery from '../hooks/useMediaQuery';
import usePagedList from '../hooks/usePagedList';
import useTableColumns from '../hooks/useTableColumns';
import useViewportFit from '../hooks/useViewportFit';
import ProjectVisitsModal from './ProfilePage/ProjectVisitsModal';
import FTEModal from './EmployeeAnalysis/FTEModal';
import useEmployeeAnalysis from './EmployeeAnalysis/useEmployeeAnalysis';
import useEmployeeAnalysisText from './EmployeeAnalysis/employeeAnalysisText';
import {
  ANALYSIS_COLUMNS,
  COLUMN_KEYS,
  DEFAULT_VISIBLE_COLUMNS,
  FILTER_KEYS,
  MIN_COLUMN_WIDTH,
  PINNED_COLUMNS,
  SORT_KEYS,
  getFilterState,
} from './EmployeeAnalysis/employeeAnalysisColumns';
import WorkerCell, { WorkerCardRow } from './EmployeeAnalysis/parts/WorkerCell';
import WorkerDetailPane from './EmployeeAnalysis/parts/WorkerDetailPane';

const COLUMNS_STORAGE_KEY = 'employeeAnalysis.tableColumns.v1';
const DOCK_PANEL_QUERY = '(min-width: 1440px)';
const FIT_BOTTOM_GAP = 24;
const FIT_MIN_HEIGHT = 400;
const COMPACT_LIST_WIDTH = 640;
const COMPACT_PAGE_SIZE = 10;

const CARD_SURFACE = 'tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-rounded-[1.75rem] tw-border tw-border-white/80 tw-bg-white/80 tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_20px_50px_-30px_rgba(15,23,42,0.35)] dark:tw-border-white/10 dark:tw-bg-[#22262f]/80 dark:tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_20px_50px_-30px_rgba(0,0,0,0.9)]';

/**
 * Çalışan Analizi (Tailwind): one row per employee with their projects,
 * roles, total visits and expense (the old grouped table's master rows);
 * search, header filters and sorting; the selected employee's panel holds
 * the assignments (each with its visits), FTE Yönetimi, İstatistikler and
 * the tariff total. Logic: useEmployeeAnalysis.
 */
const EmployeeAnalysis = () => {
  const navigate = useNavigate();
  const {
    isAdmin, workers, loading, bulkDataLoading, activeProjects, visitCounts, expenses, totalVisits, totalExpenses,
    filters, handleFilter, clearFilters, searchTerm, setSearchTerm, sortConfig, setSortConfig, sortedWorkers,
    selectedWorker, setSelectedWorkerId, fullWorker, calculateTotalRate, hasVisits, refreshWorkers, downloadExcel,
  } = useEmployeeAnalysis();
  const { language, locale, label, formatMoney } = useEmployeeAnalysisText();

  const columns = useTableColumns({
    storageKey: COLUMNS_STORAGE_KEY,
    columns: ANALYSIS_COLUMNS,
    defaultVisible: DEFAULT_VISIBLE_COLUMNS,
    minWidth: MIN_COLUMN_WIDTH,
  });
  const dockPanel = useMediaQuery(DOCK_PANEL_QUERY);
  const listCardRef = useRef(null);
  const compactList = useElementWidth(listCardRef, window.innerWidth) < COMPACT_LIST_WIDTH;
  const tableAreaRef = useRef(null);
  const fitHeight = useViewportFit(tableAreaRef, { bottomGap: FIT_BOTTOM_GAP, minHeight: FIT_MIN_HEIGHT });
  const maxCardHeight = !compactList && fitHeight ? fitHeight : undefined;
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [visitsFor, setVisitsFor] = useState(null); // { worker, project }
  const [checkingKey, setCheckingKey] = useState(null);
  const [fteFor, setFteFor] = useState(null);
  const hasLoaded = workers.length > 0 || !loading;

  const resetKey = JSON.stringify([filters, searchTerm, sortConfig]);
  const { pageRows, pageStart, currentPage, totalPages, pageSize, setPageSize, goToPage, reveal } = usePagedList(sortedWorkers, { resetKey });

  useEffect(() => {
    if (compactList) setPageSize(COMPACT_PAGE_SIZE);
  }, [compactList, setPageSize]);

  useEffect(() => {
    if (!selectedWorker) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape' || event.defaultPrevented || showFilterSheet) return;
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target?.tagName)) return;
      setSelectedWorkerId(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedWorker, showFilterSheet, setSelectedWorkerId]);

  const columnLabel = useCallback((key) => label(`columns.${key}`), [label]);

  const tableColumns = useMemo(() => columns.visibleKeys.map((key) => ({
    key,
    label: columnLabel(key),
    filter: ANALYSIS_COLUMNS[key].type ? { type: ANALYSIS_COLUMNS[key].type } : undefined,
  })), [columns.visibleKeys, columnLabel]);

  const filterChips = FILTER_KEYS
    .filter((key) => getFilterState(key, filters[key]) !== 'off')
    .map((key) => ({ key, label: columnLabel(key), value: String(filters[key]), state: 'applied' }));

  const isFilterActive = Object.values(filters).some(Boolean) || Boolean(searchTerm);

  const handlePageJump = (text) => {
    const target = parseInt(text, 10);
    if (!Number.isFinite(target) || target < 1 || target > totalPages) {
      toast.error(label('toast.pageJumpInvalid', { max: totalPages }));
      return false;
    }
    goToPage(target);
    return true;
  };

  const selectedIndex = selectedWorker ? sortedWorkers.findIndex((worker) => worker.id === selectedWorker.id) : -1;
  const selectSibling = (offset) => {
    const sibling = sortedWorkers[selectedIndex + offset];
    if (!sibling) return;
    setSelectedWorkerId(sibling.id);
    reveal(sibling.id);
  };

  const openVisits = async (project) => {
    if (!selectedWorker) return;
    const key = `${selectedWorker.id}-${project.projectId}`;
    setCheckingKey(key);
    const ok = await hasVisits(selectedWorker, project);
    setCheckingKey(null);
    if (ok) setVisitsFor({ worker: selectedWorker, project });
  };

  const workerName = (worker) => `${worker.name || ''} ${worker.surname || ''}`.trim();

  const openStatistics = () => {
    navigate('/employee-statistics', {
      state: {
        employee: { id: selectedWorker.id, name: workerName(selectedWorker) },
        dateRange: { start: '', end: '' },
      },
    });
  };

  const openFte = () => {
    const firstProject = selectedWorker.projects[0];
    setFteFor({
      id: selectedWorker.id,
      name: workerName(selectedWorker),
      project: firstProject ? { id: firstProject.projectId, title: firstProject.projectTitle } : null,
    });
  };

  const tiles = [
    { key: 'workers', label: label('stats.workers'), shortLabel: label('stats.workersShort'), value: workers.length, icon: LuUsers, gradient: 'tw-from-indigo-400 tw-to-violet-600 tw-shadow-indigo-500/30', onClick: clearFilters, active: !isFilterActive },
    { key: 'projects', label: label('stats.projects'), shortLabel: label('stats.projectsShort'), value: activeProjects, icon: LuFolderKanban, gradient: 'tw-from-sky-400 tw-to-blue-600 tw-shadow-blue-500/30' },
    { key: 'visits', label: label('stats.visits'), shortLabel: label('stats.visitsShort'), value: totalVisits, hint: bulkDataLoading ? label('stats.loading') : undefined, icon: LuCalendarCheck, gradient: 'tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30' },
    { key: 'expense', label: label('stats.expense'), shortLabel: label('stats.expenseShort'), value: totalExpenses, format: (value) => formatMoney(value).replace(' ₺', ''), suffix: '₺', hint: bulkDataLoading ? label('stats.loading') : undefined, icon: LuWallet, gradient: 'tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30' },
  ];

  const paneProps = {
    worker: selectedWorker,
    tariffWorker: selectedWorker ? fullWorker(selectedWorker.id) : null,
    totalRate: selectedWorker ? calculateTotalRate(selectedWorker.id) : 0,
    isAdmin,
    visitCounts,
    expenses,
    detailsLoading: bulkDataLoading,
    checkingKey,
    position: selectedIndex >= 0 ? { index: selectedIndex, total: sortedWorkers.length } : null,
    onPrev: selectedIndex > 0 ? () => selectSibling(-1) : undefined,
    onNext: selectedIndex >= 0 && selectedIndex < sortedWorkers.length - 1 ? () => selectSibling(1) : undefined,
    onClose: () => setSelectedWorkerId(null),
    onFte: openFte,
    onStatistics: openStatistics,
    onVisits: openVisits,
  };

  return (
    <>
      <div lang={language} className="tw-mx-auto tw-w-full tw-max-w-[1680px] tw-space-y-4 tw-font-sans tw-antialiased sm:tw-space-y-5 md:tw-px-4 lg:tw-px-6 2xl:tw-px-10">
        <PageHero
          icon={LuChartNoAxesCombined}
          gradient="tw-from-indigo-400 tw-to-violet-600 tw-shadow-indigo-500/30"
          title={label('page.title')}
          subtitle={label('page.subtitle')}
          actionsClassName="tw-flex tw-items-center tw-gap-2 max-sm:tw-w-full"
        >
          <Button onClick={downloadExcel} title={label('actions.excelHint')} className="max-sm:tw-w-full">
            <LuFileSpreadsheet aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-emerald-600 dark:tw-text-emerald-400" />
            {label('actions.excel')}
          </Button>
        </PageHero>

        <StatTiles tiles={tiles} ready={hasLoaded} className="tw-grid-cols-2 lg:tw-grid-cols-4" />

        <div ref={tableAreaRef} className="tw-flex tw-items-start tw-gap-4">
          <section ref={listCardRef} aria-label={label('table.listLabel')} style={{ maxHeight: maxCardHeight }} className={CARD_SURFACE}>
            <DataToolbar
              search={{
                value: searchTerm,
                onChange: setSearchTerm,
                placeholder: label('toolbar.search'),
                placeholderShort: label('toolbar.searchShort'),
                label: label('toolbar.search'),
                minLength: 1,
              }}
              columnMenu={{
                columns: COLUMN_KEYS.map((key) => ({ key, label: columnLabel(key) })),
                visibleColumns: columns.visibleColumns,
                onToggle: columns.toggleColumn,
                onShowAll: columns.showAllColumns,
                onReset: columns.resetColumns,
              }}
              compact={compactList}
              onOpenFilters={() => setShowFilterSheet(true)}
              chips={filterChips}
              onRemoveChip={(key) => handleFilter(key, '')}
              onClearAll={clearFilters}
              extra={(
                <SortControl
                  label={label('toolbar.sort')}
                  value={sortConfig}
                  onChange={setSortConfig}
                  options={SORT_KEYS.map((value) => ({ value, label: label(`toolbar.sortBy.${value}`) }))}
                  ascLabel={label('toolbar.asc')}
                  descLabel={label('toolbar.desc')}
                />
              )}
            />

            {compactList ? (
              <DataCardList
                label={label('table.listLabel')}
                rows={pageRows}
                renderCard={(worker) => <WorkerCardRow worker={worker} detailsLoading={bulkDataLoading} />}
                selectedId={selectedWorker?.id}
                onSelect={(worker) => setSelectedWorkerId(worker.id)}
                isLoading={loading}
                isBusy={false}
                emptyLabel={label('table.empty')}
                canClearFilters={isFilterActive}
                onClearFilters={clearFilters}
              />
            ) : (
              <DataTable
                label={label('table.listLabel')}
                rows={pageRows}
                columns={tableColumns}
                getColumnWidth={columns.getColumnWidth}
                onResizeColumn={columns.setColumnWidth}
                onResetColumnWidth={columns.resetColumnWidth}
                pinnedKeys={PINNED_COLUMNS}
                filters={filters}
                onFilterChange={handleFilter}
                getFilterState={getFilterState}
                renderCell={(worker, key) => <WorkerCell columnKey={key} worker={worker} detailsLoading={bulkDataLoading} />}
                getCellTitle={(worker, key) => {
                  if (key === 'project') return worker.projects.map((p) => p.projectTitle).join(', ') || undefined;
                  if (key === 'role') return worker.roles.join(', ') || undefined;
                  if (key === 'employee') return workerName(worker);
                  return undefined;
                }}
                selectedId={selectedWorker?.id}
                onSelect={(worker) => setSelectedWorkerId(worker.id)}
                isLoading={loading}
                isBusy={false}
                skeletonRows={pageSize}
                maxCardHeight={maxCardHeight}
                onCapacityChange={setPageSize}
                emptyLabel={label('table.empty')}
                canClearFilters={isFilterActive}
                onClearFilters={clearFilters}
                onShowColumns={columns.resetColumns}
              />
            )}

            <TablePagination
              rangeStart={pageStart + 1}
              rangeEnd={pageStart + pageRows.length}
              resultCount={sortedWorkers.length}
              rangeOf={label('table.rangeOf', { total: sortedWorkers.length.toLocaleString(locale) })}
              ofTotal={isFilterActive ? label('table.ofTotal', { total: workers.length.toLocaleString(locale) }) : undefined}
              currentPage={currentPage}
              totalPages={totalPages}
              isLoading={false}
              compact={compactList}
              onPage={goToPage}
              jump={{ label: label('table.jump'), onJump: handlePageJump }}
            />
          </section>

          {dockPanel && <WorkerDetailPane docked height={fitHeight} {...paneProps} />}
        </div>
      </div>

      {!dockPanel && <WorkerDetailPane {...paneProps} />}

      <FilterSheet
        isOpen={showFilterSheet}
        onClose={() => setShowFilterSheet(false)}
        fields={FILTER_KEYS.map((key) => ({ key, label: columnLabel(key), type: ANALYSIS_COLUMNS[key].type }))}
        filters={filters}
        getFilterState={getFilterState}
        onFilterChange={handleFilter}
        onClearAll={clearFilters}
        resultCount={sortedWorkers.length}
      />

      {visitsFor && (
        <ProjectVisitsModal
          show
          onHide={() => setVisitsFor(null)}
          project={{ id: visitsFor.project.projectId, title: visitsFor.project.projectTitle }}
          employeeId={visitsFor.worker.id}
          employeeName={workerName(visitsFor.worker)}
        />
      )}

      {fteFor && (
        <FTEModal
          show
          employee={fteFor}
          onClose={() => setFteFor(null)}
          onSaved={() => {
            setFteFor(null);
            refreshWorkers();
          }}
        />
      )}
    </>
  );
};

export default EmployeeAnalysis;
